/*
  @layer  core
  @name   En-tête fixe — élément `<sticky-header>`
  @deps   `.section-header` posée par le schéma de la section ; classes CSS
          `shopify-section-header-*` de `base.css`
  @i18n   aucune

  POURQUOI CE FICHIER EXISTE.

  `<sticky-header>` est défini par Dawn dans le `{% javascript %}` de `sections/header.liquid`.
  Depuis que `header-group.json` a basculé son entrée vers `enet-core-header`, ce fichier n'est plus
  rendu — et Shopify **n'injecte les assets empaquetés que pour les fichiers présents dans l'arbre
  de rendu de la page**. La classe n'était donc jamais définie : l'élément se rendait bien, avec son
  `data-sticky-type`, et restait un conteneur inerte. Le réglage « En-tête fixe » de l'éditeur
  n'avait aucun effet, sans la moindre erreur en console.

  Le registre des divergences affirmait l'inverse — « il reste défini dans le bundle de sections même
  si ce fichier n'est plus rendu ». C'était l'hypothèse sur laquelle reposait toute la réutilisation,
  et elle était fausse. Corrigée là-bas aussi.

  CE FICHIER EST UNE COPIE, PAS UNE RÉÉCRITURE.

  Le comportement est celui de Dawn, à la ligne près : mêmes classes posées sur `.section-header`,
  même `IntersectionObserver`, même seuil de 66 ms. Deux raisons de ne rien changer :

  1. Le **nom de l'élément reste `sticky-header`**, ce qui garde vivantes les trois intégrations de
     Dawn branchées sur `document.querySelector('sticky-header')` — `cart-notification.js`,
     `media-gallery.js` et `quick-order-list.js`. Un nom différent les aurait fait échouer en silence.
  2. Le CSS qui porte l'effet est celui de `base.css` (`shopify-section-header-sticky`,
     `-hidden`, `scrolled-past-header`). S'en écarter demanderait de le réécrire aussi.

  À resynchroniser au cherry-pick : si Dawn fait évoluer sa classe, ce fichier ne le saura pas.

  UN SEUL ÉCART, ET IL ÉTAIT DÉJÀ DANS LE DÉPÔT.

  La garde de `closeSearchModal()`. Elle vivait dans `sections/header.liquid`, qui redevient donc
  intact ; elle appartient ici, puisque c'est notre en-tête qui la rend nécessaire.
*/

class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size';
    this.headerBounds = {};

    this.setHeaderHeight();

    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky');
    }

    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.predictiveSearch = this.querySelector('predictive-search');

    this.onScrollHandler = this.onScroll.bind(this);
    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.addEventListener('scroll', this.onScrollHandler, false);

    this.createObserver();
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
  }

  disconnectedCallback() {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

    /*
      e-net. — TANT QUE LA PAGE EST VERROUILLÉE, AUCUN DÉFILEMENT N'EST DU VISITEUR.

      Dawn verrouille le défilement en posant `overflow: hidden` sur `<body>` à l'ouverture d'un
      tiroir, d'une fenêtre d'ajout rapide ou du menu mobile. Chrome supprime alors la barre de
      défilement : la fenêtre s'élargit d'une quinzaine de pixels, la page se recompose, sa hauteur
      change, et le navigateur réajuste `scrollTop` — ce qui émet un événement de défilement que
      personne n'a demandé. L'en-tête se masquait donc à l'instant même où l'on ouvrait le panier.

      Firefox ne recompose pas de la même façon, d'où un défaut qui n'existait que sur un
      navigateur — le pire genre à diagnostiquer sans y penser.

      Le garde-fou reprend celui que Dawn pose déjà une ligne plus haut pour la recherche
      prédictive : pendant qu'une surface modale est ouverte, les événements de défilement ne
      décrivent pas une intention, ils décrivent un effet de bord. `currentScrollTop` n'est
      volontairement pas mis à jour — au retour, la comparaison repart de la dernière position
      réellement voulue.
    */
    if (document.body.classList.contains('overflow-hidden')) return;

    if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (this.preventHide) return;
      requestAnimationFrame(this.hide.bind(this));
    } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
      requestAnimationFrame(this.reset.bind(this));
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
    this.closeMenuDisclosure();
    this.closeSearchModal();
  }

  reveal() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.add('shopify-section-header-sticky', 'animate');
    this.header.classList.remove('shopify-section-header-hidden');
  }

  reset() {
    if (this.headerIsAlwaysSticky) return;
    this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
  }

  closeMenuDisclosure() {
    this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
    this.disclosures.forEach((disclosure) => disclosure.close());
  }

  closeSearchModal() {
    // Notre en-tête rend un champ de recherche déployé et non une modale : sans cette garde,
    // `hide()` déréférence null à chaque défilement vers le bas.
    this.searchModal = this.searchModal || this.header.querySelector('details-modal');
    if (!this.searchModal) return;
    this.searchModal.close(false);
  }
}

/*
  Garde de double définition. Si `sections/header.liquid` venait à être rendu sur une page — un
  gabarit qui le rappellerait, un retour en arrière sur `header-group.json` —, son
  `{% javascript %}` définirait le même nom et `customElements.define` lèverait une erreur qui
  interromprait le script.
*/
if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
